import { ReactElement, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { UploadFileSvg } from '@cashier/cashier-assets';
import { addDataToUpload } from '@cashier/cashier-data-access';
import { useUIConfiguration } from '@cashier/cashier-shared';
import { Button, cn } from '@cashier/cashier-ui';

import { FileList } from './fileList';
import { useFileUploadingField } from './useFileUploadField';

import styles from './styles.module.scss';

type FileUploadingFieldProperties = {
  reference: string;
  version: 'v2' | 'v3';
  acceptTypes: {
    [key: string]: string[];
  };
  maxSize: number;
};

export const FileUploadingField = ({
  reference,
  version,
  acceptTypes,
  maxSize,
}: FileUploadingFieldProperties): ReactElement => {
  const dispatch = useDispatch();
  const { acceptedFiles, getRootProps, getInputProps, open } = useDropzone({
    accept: {
      ...acceptTypes,
    },
    multiple: false,
    maxSize,
    noClick: true,
    noKeyboard: true,
  });
  const [isUploadDisabled, setIsUploadDisabled] = useState<boolean>(false);
  const { t } = useTranslation();

  const { onFileAttached, onFileDetached } = useFileUploadingField();

  const buttonBackground = useUIConfiguration('button', 'background');

  useEffect(() => {
    if (acceptedFiles.length) {
      dispatch(
        addDataToUpload({
          file: acceptedFiles[0],
          name: reference,
        }),
      );

      onFileAttached(reference);
    }

    if (acceptedFiles.length > 0) {
      setIsUploadDisabled(true);
    }
  }, [acceptedFiles, dispatch, reference, onFileAttached]);

  const handleRemoveFile = (file: File) => {
    // @ts-expect-error: we cant use splice because acceptedFiles readonly
    acceptedFiles.splice(acceptedFiles.indexOf(file), 1);
    setIsUploadDisabled(false);
    onFileDetached(reference);
  };

  return (
    <section
      {...getRootProps({
        className: `${styles.container} ${version === 'v3' && styles.platformContainer} dropzone`,
      })}
    >
      <input {...getInputProps()} />
      <div className={styles.header}>
        <label
          className={cn({
            [styles.labelTitle]: true,
            [styles.platformLabelTitle]: version === 'v3',
          })}
        >
          {t('fileUploading.label')}
        </label>
        <p className={styles.labelBody}>{t('fileUploading.message')}</p>
      </div>

      <FileList files={[...acceptedFiles]} onFileRemove={handleRemoveFile} />

      <div className={styles.footer}>
        <div className={styles.uploadButton} data-test="file-upload-button">
          <Button
            onClick={open}
            isDisabled={isUploadDisabled}
            type="primary"
            backgroundType={buttonBackground}
          >
            <div className={styles.iconButtonContent}>
              <UploadFileSvg />
              {t('fileUploading.button')}
            </div>
          </Button>
        </div>
      </div>
    </section>
  );
};
